import React, { useMemo, useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Avatar,
  Tooltip,
  Badge,
  Box,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
} from '@material-ui/core';
import { green, grey, amber, red, blue, purple } from '@material-ui/core/colors';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import StarIcon from '@material-ui/icons/Star';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import AssignmentIcon from '@material-ui/icons/Assignment';
import SortIcon from '@material-ui/icons/Sort';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import StarsIcon from '@material-ui/icons/Stars';
import PeopleIcon from '@material-ui/icons/People';
import moment from 'moment';
import { i18n } from "../../translate/i18n";
import SafeReactApexChart from './SafeReactApexChart';
import { sanitizeApexOptions } from './sanitizeApexOptions';

const ReactApexChart = SafeReactApexChart;

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backgroundColor: theme.palette.background.paper,
    border: theme.palette.type === 'dark'
      ? `1px solid ${theme.palette.divider}`
      : '1px solid rgba(230, 230, 230, 0.5)',
    overflow: 'hidden',
    '&:hover': {
      boxShadow: '0 6px 25px 0 rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-4px)',
    },
  },
  cardContent: {
    height: '100%',
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  titleIcon: {
    marginRight: theme.spacing(1.5),
    fontSize: '1.8rem',
    borderRadius: '50%',
    padding: theme.spacing(0.5),
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(33, 150, 243, 0.1)'
      : 'rgba(33, 150, 243, 0.08)',
    color: theme.palette.primary.main,
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    letterSpacing: '0.01em',
  },
  statChip: {
    height: 24,
    fontSize: '0.75rem',
    fontWeight: 500,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  },
  chartContainer: {
    height: 220,
    marginBottom: theme.spacing(1),
  },
  listContainer: {
    flex: 1,
    overflow: 'auto',
    maxHeight: 320,
    marginTop: theme.spacing(1),
    padding: 0,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(0,0,0,0.1)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.primary.main,
      borderRadius: '10px',
    },
  },
  listItem: {
    borderRadius: 8,
    marginBottom: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    padding: theme.spacing(0, 1),
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    width: 34,
    height: 34,
  },
  online: {
    color: green[600],
    fontSize: '1.1rem',
  },
  offline: {
    color: grey[500],
    fontSize: '1.1rem',
  },
  ratingValue: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  ratingIcon: {
    color: amber[600],
    marginRight: theme.spacing(0.3),
    fontSize: '0.9rem',
  },
  ticketsValue: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  ticketsIcon: {
    color: blue[500],
    marginRight: theme.spacing(0.3),
    fontSize: '0.9rem',
  },
  reviewsValue: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  reviewsIcon: {
    color: purple[400],
    marginRight: theme.spacing(0.3),
    fontSize: '0.9rem',
  },
  timeValue: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  timeIcon: {
    color: red[400],
    marginRight: theme.spacing(0.3),
    fontSize: '0.9rem',
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  secondaryText: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  statusBadge: {
    '& .MuiBadge-badge': {
      backgroundColor: ({ online }) => online ? green[600] : grey[500],
      color: 'white',
      width: 10,
      height: 10,
      minWidth: 10,
      borderRadius: '50%',
      right: 3,
      top: 3,
    },
  },
  noDataContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '250px',
    width: '100%',
  },
  noDataText: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '250px',
    width: '100%',
  },
  sortButtonGroup: {
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sortButton: {
    fontSize: '0.7rem',
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    height: 32,
    borderRadius: 8,
  },
  sortFormControl: {
    minWidth: 170,
    height: 32,
    '& .MuiInputLabel-shrink': {
      transform: 'translate(0, -6px) scale(0.75)',
    },
    '& .MuiSelect-select': {
      paddingTop: 9,
      paddingBottom: 9,
      fontSize: '0.85rem',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
  },
  sortIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
  }
}));

const AttendantItem = React.memo(({ attendant, formatTime }) => {
  const classes = useStyles({ online: attendant.online });

  const initial = (attendant?.name && typeof attendant.name === 'string' && attendant.name.length > 0) 
    ? attendant.name.charAt(0).toUpperCase() 
    : '?';

  return (
    <ListItem className={classes.listItem} component={Paper} variant="outlined">
      <ListItemAvatar>
        <Badge
          overlap="circular"
          className={classes.statusBadge}
          variant="dot"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Avatar className={classes.avatar}>{initial}</Avatar>
        </Badge>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
            <span>{attendant?.name || 'Sem nome'}</span>
            {attendant.coverageUser ? (
              <Chip size="small" label={i18n.t("dashboard.attendants.coverageBadge") || "Cobertura"} />
            ) : null}
          </Box>
        }
        secondary={
          <Box className={classes.secondaryText} component="span">
            <Tooltip title={i18n.t("dashboard.assessments.score") || "Pontuação"}>
              <Box className={classes.ratingValue} component="span">
                <StarIcon className={classes.ratingIcon} />
                {attendant.rating || 0}
              </Box>
            </Tooltip>

            <Tooltip title={i18n.t("dashboard.assessments.totalCalls") || "Total de Atendimentos"}>
              <Box className={classes.ticketsValue} component="span">
                <AssignmentIcon className={classes.ticketsIcon} />
                {attendant.tickets || 0}
              </Box>
            </Tooltip>

            <Tooltip title={i18n.t("dashboard.assessments.ratedCalls") || "Atendimentos Avaliados"}>
              <Box className={classes.reviewsValue} component="span">
                <StarsIcon className={classes.reviewsIcon} />
                {attendant.countRating || 0}
              </Box>
            </Tooltip>

            <Tooltip title={i18n.t("dashboard.cards.averageResponseTime") || "T.M. de resposta"}>
              <Box className={classes.timeValue} component="span">
                <HourglassEmptyIcon className={classes.timeIcon} />
                {formatTime(attendant.avgWaitTime)}
              </Box>
            </Tooltip>

            <Tooltip title={i18n.t("dashboard.cards.averageServiceTime") || "T.M. Atendimento"}>
              <Box className={classes.timeValue} component="span">
                <AccessTimeIcon className={classes.timeIcon} />
                {formatTime(attendant.avgSupportTime)}
              </Box>
            </Tooltip>
          </Box>
        }
      />
      <ListItemSecondaryAction>
        <Tooltip title={attendant.online ? "Online" : "Offline"}>
          {attendant.online ?
            <CheckCircleIcon className={classes.online} /> :
            <ErrorIcon className={classes.offline} />
          }
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );
});

const TableAttendantsStatus = ({ loading, attendants, title = i18n.t("dashboard.tabs.attendants") || "Atendentes" }) => {
  const classes = useStyles();
  const theme = useTheme();

  const [sortField, setSortField] = useState('tickets');
  const [sortDirection, setSortDirection] = useState('desc');

  // ✅ Proteção: Normalizar dados de attendants para garantir que name seja sempre string
  const normalizedAttendants = useMemo(() => {
    if (!attendants || !Array.isArray(attendants)) return [];
    return attendants.map(att => ({
      ...att,
      name: (att?.name && typeof att.name === 'string') ? att.name : 'Sem nome'
    }));
  }, [attendants]);

  const formatTime = (minutes) => {
    return moment().startOf('day').add(minutes, 'minutes').format('HH[h] mm[m]');
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  const handleSortChange = (event) => {
    setSortField(event.target.value);
  };

  const sortOptions = useMemo(() => [
    { value: 'tickets', label: i18n.t("dashboard.assessments.totalCalls") || "Total de Atendimentos" },
    { value: 'rating', label: i18n.t("dashboard.assessments.score") || "Pontuação" },
    { value: 'avgSupportTime', label: i18n.t("dashboard.cards.averageServiceTime") || "T.M. Atendimento" },
    { value: 'avgWaitTime', label: i18n.t("dashboard.cards.averageResponseTime") || "T.M. de resposta" },
    { value: 'countRating', label: i18n.t("dashboard.assessments.ratedCalls") || "Chamados Avaliados" },
  ], []);

  const sortedAttendants = useMemo(() => {
    if (!normalizedAttendants || normalizedAttendants.length === 0) return [];

    const base = [...normalizedAttendants].sort((a, b) => {
      const aValue = a[sortField] || 0;
      const bValue = b[sortField] || 0;

      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });

    return [...base].sort((a, b) => {
      if (Boolean(a.coverageUser) === Boolean(b.coverageUser)) return 0;
      return a.coverageUser ? 1 : -1;
    });
  }, [normalizedAttendants, sortField, sortDirection]);

  const chartData = useMemo(() => {
    if (!sortedAttendants || sortedAttendants.length === 0) {
      return {
        categories: [],
        series: []
      };
    }

    const topAttendants = sortedAttendants.slice(0, 5);

    const selectedOption = sortOptions.find(option => option.value === sortField);
    const metricLabel = selectedOption ? selectedOption.label : "";

    let seriesData;
    switch (sortField) {
      case 'rating':
        seriesData = topAttendants.map(a => a.rating || 0);
        break;
      case 'avgWaitTime':
        seriesData = topAttendants.map(a => a.avgWaitTime || 0);
        break;
      case 'avgSupportTime':
        seriesData = topAttendants.map(a => a.avgSupportTime || 0);
        break;
      case 'countRating':
        seriesData = topAttendants.map(a => a.countRating || 0);
        break;
      case 'tickets':
      default:
        seriesData = topAttendants.map(a => a.tickets || 0);
        break;
    }

    return {
      categories: topAttendants.map(a => {
        const name = a?.name;
        return (name && typeof name === 'string') ? name : 'Sem nome';
      }),
      series: [{
        name: metricLabel || '',
        data: seriesData
      }],
      selectedField: sortField
    };
  }, [sortedAttendants, sortField, sortOptions]);

  const chartOptions = useMemo(() => ({
    chart: {
      type: 'bar',
      fontFamily: theme.typography.fontFamily,
      foreColor: theme.palette.text.secondary,
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 500,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
    },
    title: {
      text: (sortOptions.find(option => option.value === sortField)?.label || "").toString(),
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: theme.palette.text.primary
      },
      offsetY: 10,
      offsetX: 10,
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: '70%',
        distributed: true,
        dataLabels: {
          position: 'top',
          offsetX: 15,
        },
      },
    },
    colors: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.info.main,
      theme.palette.warning.main,
      theme.palette.error.main,
    ],
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        if (chartData.selectedField === 'avgWaitTime' || chartData.selectedField === 'avgSupportTime') {
          return moment().startOf('day').add(val, 'minutes').format('HH[h] mm[m]');
        } else if (chartData.selectedField === 'rating') {
          return val.toFixed(1);
        }
        return val;
      },
      style: {
        fontSize: '11px',
        fontFamily: theme.typography.fontFamily,
        fontWeight: 500,
        colors: [theme.palette.text.secondary]
      },
      offsetX: 15,
      background: {
        enabled: true,
        foreColor: theme.palette.background.paper,
        borderRadius: 4,
        padding: 4,
        opacity: 0.9,
        borderWidth: 1,
        borderColor: theme.palette.divider,
        dropShadow: {
          enabled: true,
          top: 0,
          left: 0,
          blur: 3,
          opacity: 0.2
        }
      }
    },
    xaxis: {
      categories: chartData.categories.map(cat => (cat && typeof cat === 'string') ? cat : 'Sem nome'),
      labels: {
        style: {
          fontSize: '10px',
          fontFamily: theme.typography.fontFamily,
          colors: theme.palette.text.primary,
        }
      },
      axisBorder: {
        show: true,
        color: theme.palette.divider,
      },
      axisTicks: {
        show: true,
        color: theme.palette.divider,
      },
    },
    grid: {
      show: true,
      borderColor: theme.palette.divider,
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      },
      padding: {
        left: 10,
      }
    },
    tooltip: {
      theme: (() => {
        const themeValue = theme.palette?.type || theme.palette?.mode;
        return (themeValue && typeof themeValue === 'string') ? themeValue.toLowerCase() : 'light';
      })(),
      y: {
        formatter: function (val) {
          if (chartData.selectedField === 'avgWaitTime' || chartData.selectedField === 'avgSupportTime') {
            return moment().startOf('day').add(val, 'minutes').format('HH[h] mm[m]');
          } else if (chartData.selectedField === 'rating') {
            return val.toFixed(1) + ' ★';
          } else if (chartData.selectedField === 'tickets' || chartData.selectedField === 'countRating') {
            return val + ' ' + (val === 1 ? 'atendimento' : 'atendimentos');
          }
          return val;
        }
      },
      style: {
        fontSize: '12px',
        fontFamily: theme.typography.fontFamily,
      },
      marker: {
        show: true,
      }
    },
    legend: {
      show: false,
    }
  }), [theme, chartData, sortField, sortOptions]);

  if (loading) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <PeopleIcon className={classes.titleIcon} />
              <Typography className={classes.title}>
                {title}
              </Typography>
            </div>
          </div>
          <div className={classes.loadingContainer}>
            <CircularProgress size={40} thickness={4} color="primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!attendants || attendants.length === 0) {
    return (
      <Card className={classes.card}>
        <CardContent className={classes.cardContent}>
          <div className={classes.header}>
            <div className={classes.titleContainer}>
              <PeopleIcon className={classes.titleIcon} />
              <Typography className={classes.title}>
                {title}
              </Typography>
            </div>
          </div>
          <div className={classes.noDataContainer}>
            <Typography className={classes.noDataText}>
              {i18n.t("dashboard.noData") || "Sem dados disponíveis"}
            </Typography>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <div className={classes.header}>
          <div className={classes.titleContainer}>
            <PeopleIcon className={classes.titleIcon} />
            <Typography className={classes.title}>
              {title}
            </Typography>
          </div>
          <Chip
            size="small"
            label={`${attendants.length} atendentes`}
            className={classes.statChip}
            variant="outlined"
          />
        </div>

        <Box className={classes.sortButtonGroup}>
          <FormControl variant="outlined" size="small" className={classes.sortFormControl}>
            <Select
              value={sortField}
              onChange={handleSortChange}
              displayEmpty
              startAdornment={<SortIcon className={classes.sortIcon} />}
            >
              {sortOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            size="small"
            variant="contained"
            color={sortDirection === 'desc' ? 'primary' : 'default'}
            onClick={toggleSortDirection}
            endIcon={sortDirection === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
            className={classes.sortButton}
          >
            {sortDirection === 'desc'
              ? (i18n.t("dashboard.sort.descending") || "Decrescente")
              : (i18n.t("dashboard.sort.ascending") || "Crescente")}
          </Button>
        </Box>

        {chartData.categories.length > 0 && (
          <div className={classes.chartContainer}>
            <ReactApexChart
              options={sanitizeApexOptions(chartOptions)}
              series={chartData.series.map(s => ({
                ...s,
                name: (s?.name && typeof s.name === 'string') ? s.name : '',
                data: (s?.data || []).map(v => {
                  if (v == null || v === undefined || isNaN(v)) return 0;
                  return typeof v === 'number' ? v : parseFloat(v) || 0;
                })
              }))}
              type="bar"
              height="100%"
              width="100%"
            />
          </div>
        )}

        <Divider className={classes.divider} />

        <Box component={List} className={classes.listContainer}>
          {sortedAttendants.map((attendant, index) => (
            <AttendantItem
              key={index}
              attendant={attendant}
              formatTime={formatTime}
            />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default React.memo(TableAttendantsStatus);